import { useState, useEffect, useCallback } from 'react'
import {
  Alert, Button, Form, Input, Modal, Popconfirm, Select, Space, Statistic, Table, Tabs, Tag, TreeSelect, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import * as uiApi from '@/api/uiAutomation'
import { useProjectStore } from '@/stores/project'
import type { UIAILog, UIAIUsageSummary, UICacheKey, UIHealLog, UIPage } from '@/types'
import '../../styles/aurora-page.css'

const { Title, Text } = Typography

/** 自愈状态: healed=自愈成功/heal_failed=自愈失败 */
const healStatusMap: Record<string, { text: string; color: string }> = {
  healed: { text: '自愈成功', color: 'success' },
  heal_failed: { text: '自愈失败', color: 'error' },
}

/** AI 能力名称 */
const capabilityTextMap: Record<string, string> = {
  step_translation: '步骤翻译',
  assertion_primary: '主模型断言',
  assertion_secondary: '副模型断言',
  heal_translation: '自愈翻译',
}

const CAPABILITY_OPTIONS = [
  { value: 'step_translation', label: '步骤翻译' },
  { value: 'assertion_primary', label: '主模型断言' },
  { value: 'assertion_secondary', label: '副模型断言' },
  { value: 'heal_translation', label: '自愈翻译' },
]

/** 动作 JSON 摘要（表格内单行展示） */
const actionSummary = (raw: string) => {
  if (!raw) return ''
  try {
    const obj = JSON.parse(raw)
    return [obj.action, obj.target, obj.value].filter(Boolean).join(' → ').slice(0, 80)
  } catch {
    return raw.slice(0, 80)
  }
}

/**
 * 自愈与缓存（二期）
 * - 顶部统计卡：AI 用量汇总（真实调用/Token/缓存命中/自愈次数）
 * - 自愈记录：AI 步骤执行失败后的重译明细
 * - AI 用量日志：每次真实 LLM 调用一条（缓存命中不计调用）
 * - 步骤缓存：AI 翻译结果缓存管理（查看/失效）
 */
export default function UIHealing() {
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0

  // 用量汇总
  const [summary, setSummary] = useState<UIAIUsageSummary | null>(null)

  // 自愈记录列表
  const [healLogs, setHealLogs] = useState<UIHealLog[]>([])
  const [healLoading, setHealLoading] = useState(false)
  const [healPage, setHealPage] = useState(1)
  const [healPageSize, setHealPageSize] = useState(10)
  const [healTotal, setHealTotal] = useState(0)
  const [healStatusFilter, setHealStatusFilter] = useState<string>('')

  // AI 用量日志列表
  const [aiLogs, setAiLogs] = useState<UIAILog[]>([])
  const [aiLoading, setAiLoading] = useState(false)
  const [aiPage, setAiPage] = useState(1)
  const [aiPageSize, setAiPageSize] = useState(10)
  const [aiTotal, setAiTotal] = useState(0)
  const [capabilityFilter, setCapabilityFilter] = useState<string>('')

  // 步骤缓存列表
  const [cacheKeys, setCacheKeys] = useState<UICacheKey[]>([])
  const [cacheLoading, setCacheLoading] = useState(false)
  const [cachePage, setCachePage] = useState(1)
  const [cachePageSize, setCachePageSize] = useState(10)
  const [cacheTotal, setCacheTotal] = useState(0)
  const [cacheKeyword, setCacheKeyword] = useState('')
  // 选中待删除的缓存 key
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [deleting, setDeleting] = useState(false)

  // 动作 JSON 查看弹窗
  const [actionView, setActionView] = useState<{ title: string; content: string } | null>(null)

  // 三期 B：自愈沉淀为元素弹窗
  const [promoteOpen, setPromoteOpen] = useState(false)
  const [promoteRecord, setPromoteRecord] = useState<UIHealLog | null>(null)
  const [promoteSubmitting, setPromoteSubmitting] = useState(false)
  const [pageTree, setPageTree] = useState<UIPage[]>([])
  const [promoteForm] = Form.useForm()
  // 三期 B：自愈频繁提示（同一脚本同一步骤近期自愈 ≥3 次 → 建议沉淀为元素）
  const [frequentHeals, setFrequentHeals] = useState<{ scriptId: number; scriptName: string; stepIndex: number; count: number }[]>([])

  const fetchSummary = useCallback(async () => {
    if (!projectId) return
    try {
      setSummary(await uiApi.getAIUsageSummary(projectId))
    } catch {
      setSummary(null)
    }
  }, [projectId])

  const fetchHealLogs = useCallback(async () => {
    if (!projectId) return
    setHealLoading(true)
    try {
      const res = await uiApi.listHealLogs({
        project_id: projectId, page: healPage, page_size: healPageSize,
        status: healStatusFilter || undefined,
      })
      setHealLogs(res.data)
      setHealTotal(res.total)
    } finally {
      setHealLoading(false)
    }
  }, [projectId, healPage, healPageSize, healStatusFilter])

  // 三期 B：自愈频繁识别（取最近 100 条，同一脚本同一步骤自愈成功 ≥3 次提示固化）
  const fetchFrequentHeals = useCallback(async () => {
    if (!projectId) return
    try {
      const res = await uiApi.listHealLogs({ project_id: projectId, page: 1, page_size: 100 })
      const counts = new Map<string, { count: number; scriptId: number; scriptName: string; stepIndex: number }>()
      res.data.forEach((r) => {
        if (r.status !== 'healed') return
        const k = `${r.script_id}:${r.step_index}`
        const prev = counts.get(k)
        counts.set(k, {
          count: (prev?.count || 0) + 1,
          scriptId: r.script_id,
          scriptName: r.script_name,
          stepIndex: r.step_index,
        })
      })
      setFrequentHeals(
        [...counts.values()]
          .filter((v) => v.count >= 3)
          .sort((a, b) => b.count - a.count)
          .slice(0, 5),
      )
    } catch {
      setFrequentHeals([])
    }
  }, [projectId])

  const fetchAiLogs = useCallback(async () => {
    if (!projectId) return
    setAiLoading(true)
    try {
      const res = await uiApi.listAILogs({
        project_id: projectId, page: aiPage, page_size: aiPageSize,
        capability: capabilityFilter || undefined,
      })
      setAiLogs(res.data)
      setAiTotal(res.total)
    } finally {
      setAiLoading(false)
    }
  }, [projectId, aiPage, aiPageSize, capabilityFilter])

  const fetchCacheKeys = useCallback(async () => {
    if (!projectId) return
    setCacheLoading(true)
    try {
      const res = await uiApi.listCacheKeys({
        project_id: projectId, page: cachePage, page_size: cachePageSize, keyword: cacheKeyword,
      })
      setCacheKeys(res.data)
      setCacheTotal(res.total)
    } finally {
      setCacheLoading(false)
    }
  }, [projectId, cachePage, cachePageSize, cacheKeyword])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => {
    fetchHealLogs()
  }, [fetchHealLogs])

  useEffect(() => {
    fetchFrequentHeals()
  }, [fetchFrequentHeals])

  useEffect(() => {
    fetchAiLogs()
  }, [fetchAiLogs])

  useEffect(() => {
    fetchCacheKeys()
  }, [fetchCacheKeys])

  // 切页/筛选变化时清空缓存勾选
  useEffect(() => {
    setSelectedKeys([])
  }, [cachePage, cachePageSize, cacheKeyword])

  const handleClearAllCache = async () => {
    setDeleting(true)
    try {
      const res = await uiApi.deleteCacheKeys(projectId)
      message.success(`已失效 ${res.deleted} 条缓存`)
      setCachePage(1)
      fetchCacheKeys()
      fetchSummary()
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteSelected = async () => {
    if (!selectedKeys.length) return
    setDeleting(true)
    try {
      const res = await uiApi.deleteCacheKeys(projectId, selectedKeys)
      message.success(`已失效 ${res.deleted} 条缓存`)
      setSelectedKeys([])
      fetchCacheKeys()
      fetchSummary()
    } finally {
      setDeleting(false)
    }
  }

  /** 三期 B：打开沉淀为元素弹窗（预填元素名/描述，从自愈动作推断名称） */
  const openPromote = (r: UIHealLog) => {
    setPromoteRecord(r)
    let suggested = ''
    try {
      const act = JSON.parse(r.new_action)
      const sel = String(act.selector || '')
      suggested = sel.split(/[>:\s.#\[\]"']/).filter(Boolean).pop() || sel
    } catch {
      suggested = ''
    }
    promoteForm.setFieldsValue({
      name: (suggested || `自愈元素-步骤${r.step_index}`).slice(0, 50),
      description: `自愈沉淀：${r.step_description || ''}（步骤 #${r.step_index}）`.slice(0, 200),
    })
    setPromoteOpen(true)
    uiApi.listPages(projectId).then((pages) => setPageTree(pages)).catch(() => setPageTree([]))
  }

  /** 三期 B：提交沉淀（自愈动作 → 元素库元素） */
  const handlePromote = async () => {
    if (!promoteRecord) return
    const values = await promoteForm.validateFields()
    setPromoteSubmitting(true)
    try {
      const { element, message: tip } = await uiApi.promoteHealElement(promoteRecord.id, {
        name: values.name,
        page_id: values.page_id || 0,
        description: values.description || '',
      })
      // 弹出后端返回的提示（如"已沉淀为元素库元素"），缺失时回退前端文案
      message.success(tip || `已沉淀为元素「${element.name}」（元素 #${element.id}），可将对应步骤转为元素步骤`)
      setPromoteOpen(false)
      // 刷新列表：操作列按钮变为"已沉淀"标记
      fetchHealLogs()
      fetchFrequentHeals()
    } catch (e) {
      // HTTP/业务错误已由全局拦截器弹出提示，此处仅阻断弹窗关闭
      console.warn('沉淀失败', e)
    } finally {
      setPromoteSubmitting(false)
    }
  }

  /** 页面树 → TreeSelect 数据（不选则视为未分组） */
  interface PageTreeNode {
    value: number
    title: string
    children?: PageTreeNode[]
  }
  const toTreeData = (pages: UIPage[]): PageTreeNode[] =>
    pages.map((p) => ({
      value: p.id,
      title: p.name,
      children: p.children && p.children.length ? toTreeData(p.children) : undefined,
    }))

  const tokenText = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(1)}k` : String(n))

  const statCards = [
    { title: '真实 LLM 调用', value: summary?.total_calls ?? 0, suffix: '次' },
    { title: '输入 Token', value: summary?.prompt_tokens ?? 0, suffix: '' },
    { title: '输出 Token', value: summary?.completion_tokens ?? 0, suffix: '' },
    { title: '缓存命中', value: summary?.cache_hits ?? 0, suffix: '次' },
    { title: '自愈触发', value: summary?.healed_calls ?? 0, suffix: '次' },
  ]

  const healColumns: ColumnsType<UIHealLog> = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '用例', dataIndex: 'script_name', width: 140, ellipsis: true },
    {
      title: '步骤',
      dataIndex: 'step_index',
      width: 70,
      render: (v: number) => <Tag>#{v}</Tag>,
    },
    { title: '步骤描述', dataIndex: 'step_description', width: 160, ellipsis: true },
    {
      title: '失败动作',
      dataIndex: 'old_action',
      width: 160,
      ellipsis: true,
      render: (v: string, r: UIHealLog) => (
        <a
          onClick={() => setActionView({ title: `步骤 ${r.step_index} 失败动作`, content: v ? JSON.stringify(JSON.parse(v), null, 2) : '' })}
          style={{ fontSize: 13 }}
        >
          {actionSummary(v) || '-'}
        </a>
      ),
    },
    {
      title: '自愈动作',
      dataIndex: 'new_action',
      width: 160,
      ellipsis: true,
      render: (v: string, r: UIHealLog) =>
        r.status === 'healed' ? (
          <a
            onClick={() => setActionView({ title: `步骤 ${r.step_index} 自愈动作`, content: v ? JSON.stringify(JSON.parse(v), null, 2) : '' })}
            style={{ fontSize: 13 }}
          >
            {actionSummary(v) || '-'}
          </a>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>-</span>
        ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: string) => {
        const s = healStatusMap[v] || { text: v, color: 'default' }
        return <Tag color={s.color}>{s.text}</Tag>
      },
    },
    {
      // 三期 A：自愈级别（locator=定位器级重写 / description=描述级整步重译）
      title: '级别',
      dataIndex: 'heal_level',
      width: 100,
      render: (v: string, r: UIHealLog) =>
        r.status === 'healed' ? (
          <Tag color={v === 'locator' ? 'green' : 'orange'}>{v === 'locator' ? '定位器级' : '描述级'}</Tag>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>-</span>
        ),
    },
    {
      title: '失败原因',
      dataIndex: 'error_message',
      ellipsis: true,
      render: (v: string) => (
        <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>{v || '-'}</span>
      ),
    },
    {
      title: '时间',
      dataIndex: 'create_time',
      width: 160,
      render: (v: string) => (
        <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>
          {v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-'}
        </span>
      ),
    },
    {
      // 三期 B：自愈成功的动作一键沉淀为元素库元素（已沉淀显示元素标记，按钮消失）
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_: unknown, r: UIHealLog) => {
        if (r.status !== 'healed') return null
        if (r.promoted_element_id) {
          return <Tag color="green">已沉淀 #{r.promoted_element_id}</Tag>
        }
        return (
          <Button type="link" size="small" style={{ padding: '0 4px' }} onClick={() => openPromote(r)}>沉淀为元素</Button>
        )
      },
    },
  ]

  const aiLogColumns: ColumnsType<UIAILog> = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '用例', dataIndex: 'script_name', width: 160, ellipsis: true },
    {
      title: '能力',
      dataIndex: 'capability',
      width: 110,
      render: (v: string) => {
        const colorMap: Record<string, string> = {
          step_translation: 'blue', assertion_primary: 'geekblue',
          assertion_secondary: 'cyan', heal_translation: 'orange',
        }
        return <Tag color={colorMap[v] || 'default'}>{capabilityTextMap[v] || v}</Tag>
      },
    },
    { title: '模型', dataIndex: 'model', width: 140, ellipsis: true },
    { title: '输入 Token', dataIndex: 'prompt_tokens', width: 100, render: (v: number) => <span style={{ fontSize: 13 }}>{v}</span> },
    { title: '输出 Token', dataIndex: 'completion_tokens', width: 100, render: (v: number) => <span style={{ fontSize: 13 }}>{v}</span> },
    {
      title: '耗时',
      dataIndex: 'duration_ms',
      width: 90,
      render: (v: number) => <span style={{ fontSize: 13 }}>{v > 0 ? `${(v / 1000).toFixed(2)}s` : '-'}</span>,
    },
    {
      title: '自愈',
      dataIndex: 'healed',
      width: 70,
      render: (v: boolean) => (v ? <Tag color="orange">是</Tag> : <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>-</span>),
    },
    {
      title: '置信度',
      dataIndex: 'confidence_score',
      width: 80,
      render: (v: number | null) => (v === null || v === undefined ? '-' : <Tag color={v >= 70 ? 'success' : 'warning'}>{v}</Tag>),
    },
    {
      title: '理由摘要',
      dataIndex: 'reasoning',
      ellipsis: true,
      render: (v: string) => (
        <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>{v || '-'}</span>
      ),
    },
    {
      title: '时间',
      dataIndex: 'create_time',
      width: 160,
      render: (v: string) => (
        <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>
          {v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-'}
        </span>
      ),
    },
  ]

  const cacheColumns: ColumnsType<UICacheKey> = [
    { title: '脚本', dataIndex: 'script_id', width: 80, render: (v: number) => <Tag>#{v}</Tag> },
    { title: '步骤', dataIndex: 'step_index', width: 70, render: (v: number) => <Tag>#{v}</Tag> },
    {
      title: '动作',
      dataIndex: 'action',
      width: 100,
      render: (v: string) => (v ? <Tag color="blue">{v}</Tag> : '-'),
    },
    {
      title: '翻译理由',
      dataIndex: 'reason',
      ellipsis: true,
      render: (v: string) => <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>{v || '-'}</span>,
    },
    {
      title: '剩余有效期',
      dataIndex: 'ttl',
      width: 110,
      render: (v: number) => {
        if (v === -1) return <Tag color="purple">永不过期</Tag>
        if (v === -2) return <Tag>已过期</Tag>
        const hours = Math.floor(v / 3600)
        const mins = Math.floor((v % 3600) / 60)
        return <span style={{ fontSize: 13 }}>{hours > 0 ? `${hours}时${mins}分` : `${mins}分`}</span>
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 110,
      render: (_, r: UICacheKey) => (
        <Popconfirm title="确定失效该条缓存？" onConfirm={async () => {
          await uiApi.deleteCacheKeys(projectId, [r.key])
          message.success('已失效该条缓存')
          fetchCacheKeys()
          fetchSummary()
        }} okText="确定" cancelText="取消" okButtonProps={{ danger: true }}>
          <Button type="link" size="small" danger>失效</Button>
        </Popconfirm>
      ),
    },
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>自愈与缓存</Title>
          <Tag color="orange">AI 自愈重译</Tag>
          <Tag color="blue">步骤缓存管理</Tag>
        </div>
        <Space>
          <Button onClick={() => { fetchSummary(); fetchHealLogs(); fetchAiLogs(); fetchCacheKeys() }}>刷新</Button>
        </Space>
      </div>

      {/* 用量汇总统计卡 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        {statCards.map((c) => (
          <div
            key={c.title}
            style={{
              flex: '1 1 150px', minWidth: 150,
              background: 'var(--au-header-bg)',
              border: '1px solid var(--au-border)',
              borderRadius: 10,
              padding: '12px 16px',
            }}
          >
            <Statistic
              title={<span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>{c.title}</span>}
              value={c.value}
              formatter={(v) => `${tokenText(Number(v))}${c.suffix}`}
              valueStyle={{ fontSize: 22, fontWeight: 600, color: 'var(--au-slate)' }}
            />
          </div>
        ))}
      </div>

      <div className="content-card">
        <Tabs
          defaultActiveKey="heal"
          tabBarStyle={{ paddingLeft: 22 }}
          items={[
            {
              key: 'heal',
              label: `自愈记录（${healTotal}）`,
              children: (
                <>
                  <div className="search-bar" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Select
                      placeholder="自愈状态"
                      allowClear
                      style={{ width: 130 }}
                      value={healStatusFilter || undefined}
                      onChange={(v) => { setHealStatusFilter(v || ''); setHealPage(1) }}
                      options={[
                        { value: 'healed', label: '自愈成功' },
                        { value: 'heal_failed', label: '自愈失败' },
                      ]}
                    />
                    <Button
                      type="primary"
                      onClick={() => setHealPage(1)}
                      style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}
                    >
                      查询
                    </Button>
                    {/* 自愈频繁提示内联在筛选栏右侧，不单独占一行 */}
                    {frequentHeals.length > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
                        message={
                          <span style={{ fontSize: 12 }}>
                            检测到 {frequentHeals.length} 个自愈频繁的步骤，建议沉淀为元素步骤（不再依赖 AI）：
                            {frequentHeals.map((f) => `${f.scriptName || `脚本 #${f.scriptId}`} · 步骤 #${f.stepIndex}（近期自愈成功 ${f.count} 次）`).join('；')}
                          </span>
                        }
                      />
                    )}
                  </div>
                  <Table
                    columns={healColumns}
                    dataSource={healLogs}
                    rowKey="id"
                    loading={healLoading}
                    scroll={{ y: '100%' }}
                    pagination={{
                      current: healPage,
                      total: healTotal,
                      pageSize: healPageSize,
                      showSizeChanger: true,
                      showTotal: (t: number) => `共 ${t} 条`,
                      onChange: (p, ps) => { setHealPage(p); setHealPageSize(ps) },
                    }}
                  />
                </>
              ),
            },
            {
              key: 'ai-logs',
              label: `AI 用量日志（${aiTotal}）`,
              children: (
                <>
                  <div className="search-bar" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Select
                      placeholder="能力筛选"
                      allowClear
                      style={{ width: 150 }}
                      value={capabilityFilter || undefined}
                      onChange={(v) => { setCapabilityFilter(v || ''); setAiPage(1) }}
                      options={CAPABILITY_OPTIONS}
                    />
                    <Button
                      type="primary"
                      onClick={() => setAiPage(1)}
                      style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}
                    >
                      查询
                    </Button>
                  </div>
                  <Table
                    columns={aiLogColumns}
                    dataSource={aiLogs}
                    rowKey="id"
                    loading={aiLoading}
                    scroll={{ y: '100%' }}
                    pagination={{
                      current: aiPage,
                      total: aiTotal,
                      pageSize: aiPageSize,
                      showSizeChanger: true,
                      showTotal: (t: number) => `共 ${t} 条`,
                      onChange: (p, ps) => { setAiPage(p); setAiPageSize(ps) },
                    }}
                  />
                </>
              ),
            },
            {
              key: 'cache',
              label: `步骤缓存（${cacheTotal}）`,
              children: (
                <>
                  <div className="search-bar" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Input
                      allowClear
                      placeholder="按缓存 key 关键词过滤"
                      value={cacheKeyword}
                      onChange={(e) => { setCacheKeyword(e.target.value); setCachePage(1) }}
                      style={{ width: 260 }}
                    />
                    <Button
                      type="primary"
                      onClick={() => setCachePage(1)}
                      style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}
                    >
                      查询
                    </Button>
                    {/* 缓存机制说明放在搜索框与右侧操作按钮之间的中间区域，不单独占一行 */}
                    <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--au-slate-muted)', lineHeight: '20px' }}>
                      AI 步骤翻译结果按「项目 / 缓存版本 / 脚本 / 步骤」缓存，命中后不再调用 AI；页面改版导致翻译失效时，可在此失效对应缓存，下次执行将重新调用 AI 翻译。
                    </div>
                    <Popconfirm
                      title={`确定失效选中的 ${selectedKeys.length} 条缓存？`}
                      onConfirm={handleDeleteSelected}
                      disabled={!selectedKeys.length}
                      okText="确定"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                    >
                      <Button danger disabled={!selectedKeys.length}>
                        失效选中（{selectedKeys.length}）
                      </Button>
                    </Popconfirm>
                    <Popconfirm
                      title="确定失效项目下全部步骤缓存？"
                      onConfirm={handleClearAllCache}
                      disabled={cacheTotal === 0}
                      okText="确定"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                    >
                      <Button danger disabled={cacheTotal === 0} loading={deleting}>全部失效</Button>
                    </Popconfirm>
                  </div>
                  <Table
                    columns={cacheColumns}
                    dataSource={cacheKeys}
                    rowKey="key"
                    loading={cacheLoading}
                    scroll={{ y: '100%' }}
                    rowSelection={{
                      selectedRowKeys: selectedKeys,
                      onChange: (keys) => setSelectedKeys(keys as string[]),
                      // 全局表格单元格左右各 22px 内边距，加宽选择列避免复选框被裁切
                      columnWidth: 60,
                      selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
                    }}
                    pagination={{
                      current: cachePage,
                      total: cacheTotal,
                      pageSize: cachePageSize,
                      showSizeChanger: true,
                      showTotal: (t: number) => `共 ${t} 条`,
                      onChange: (p, ps) => { setCachePage(p); setCachePageSize(ps) },
                    }}
                  />
                </>
              ),
            },
          ]}
        />
      </div>

      {/* 动作 JSON 查看弹窗 */}
      <Modal
        title={actionView?.title || ''}
        open={!!actionView}
        onCancel={() => setActionView(null)}
        footer={<Button type="primary" onClick={() => setActionView(null)}>关闭</Button>}
        width={640}
        rootClassName="aurora-modal-wrap"
        className="aurora-modal"
      >
        <pre
          style={{
            margin: 0,
            maxHeight: 420,
            overflow: 'auto',
            borderRadius: 10,
            padding: '12px 14px',
            fontSize: 12,
            lineHeight: '20px',
            fontFamily: 'Consolas, Menlo, monospace',
            background: '#10192b',
            color: '#c8d6e5',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {actionView?.content || '（空）'}
        </pre>
      </Modal>
      {/* 三期 B：自愈沉淀为元素弹窗 */}
      <Modal
        title="沉淀为元素库元素"
        open={promoteOpen}
        onCancel={() => setPromoteOpen(false)}
        onOk={handlePromote}
        okText="沉淀"
        cancelText="取消"
        confirmLoading={promoteSubmitting}
        okButtonProps={{ style: { background: 'var(--au-primary)', borderColor: 'var(--au-primary)' } }}
        width={520}
        rootClassName="aurora-modal-wrap"
        className="aurora-modal"
      >
        <div style={{ fontSize: 12, color: 'var(--au-slate-muted)', marginBottom: 12, lineHeight: '20px' }}>
          将自愈成功的定位表达式写入元素库（{promoteRecord?.new_action ? '来源：自愈动作' : ''}），之后可在用例编辑中把对应步骤转为元素步骤，不再依赖 AI 翻译。
        </div>
        <Form form={promoteForm} layout="vertical">
          <Form.Item
            name="name"
            label="元素名称"
            rules={[{ required: true, message: '请输入元素名称' }, { max: 255, message: '名称过长' }]}
          >
            <Input placeholder="如：登录按钮" />
          </Form.Item>
          <Form.Item name="page_id" label="所属页面">
            <TreeSelect
              treeData={toTreeData(pageTree)}
              treeDefaultExpandAll
              allowClear
              placeholder="选择所属页面（不选则未分组）"
            />
          </Form.Item>
          <Form.Item name="description" label="元素描述">
            <Input.TextArea rows={2} maxLength={2000} placeholder="元素用途描述" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
